import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { openMinSupportChat } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { ONBOARDING_DESIGN_MODE } from "@/lib/onboarding-design-mode";
import { buildPlanComparisonPath } from "@/lib/plan-comparison";
import { MIN_PLAN_BEHAVIOR_URL, MIN_TRUST_FAQS } from "@/lib/trust-content";

const planLink = { text: "See full plan behavior.", href: MIN_PLAN_BEHAVIOR_URL, planBehavior: true };

const productFaqs = [
  {
    q: "How do I replace an expired card?",
    a: ["Open Billing and choose Update card. If billing is paused, follow the steps there to resume it. ", { text: "Manage billing", href: "/billing#payment-method" }],
  },
  {
    q: "Can I connect my own AI agent?",
    a: ["Connect your own AI agent with Pro. Your agent handles schedules and approvals.", { text: "Connect my agent", href: "/agent" }],
  },
  {
    q: "What do I earn from referrals?",
    a: "When your friend links WhatsApp, you both get one extra week of Fun. When they first pay, you earn a month of their plan.",
  },
  {
    q: "Can Min translate voice notes?",
    a: "When a summary is sent, Min can translate it into the target language if that language is supported.",
  },
  {
    q: "What happens to banked rewards if I am paying?",
    a: "Banked time starts after paid access ends. It does not reduce automatic payments.",
  },
  {
    q: "How does Min manage my data via WhatsApp?",
    a: "Min uses the voice note itself and, when helpful, recent chat context to keep the summary accurate. Pro also lets you connect your own AI agent.",
  },
  {
    q: "Can Min summarize voice notes I receive?",
    a: [
      "Yes. Fun includes inbound voice-note summaries: when someone sends you a voice note, Min can DM you the summary on WhatsApp. ",
      planLink,
    ],
  },
  {
    q: "Can Min summarize voice notes I send?",
    a: "Yes. When you send a voice note, Min can follow with a written summary or transcript after your message.",
  },
  {
    q: "Can I get full transcripts, not just summaries?",
    a: [
      "Yes. Fun includes full transcripts, and your saved preferences decide whether Min sends summaries, transcripts, or both. ",
      planLink,
    ],
  },
  {
    q: "Can I see who I referred?",
    a: "Your Referrals page shows linked friends and the free time you earned.",
  },
  {
    q: "What happens when my free Fun time ends?",
    a: "Min sends reminders and a payment link before your free Fun time ends. You can pay without signing in. Free keeps outgoing summaries.",
  },
  {
    q: "Can I stop Min from automatically summarizing every voice note?",
    a: [
      "Yes. On Fun, turn Automatic off for incoming or outgoing voice notes. Min captures and transcribes right away, then waits until you react with Ⓜ️ before sending results. ",
      planLink,
    ],
  },
  {
    q: "Why is there a Ⓜ️ emoji on my summaries?",
    a: [
      "The Ⓜ️ mark is Min's signature. It helps people know where the summary came from. Pro can remove Min branding if you want the output clean. ",
      planLink,
    ],
  },
  {
    q: "Can I control which chats Min works in?",
    a: [
      "Your free month of Fun uses your global defaults. Pro adds per-chat overrides for inbound, outbound, branding, and group-chat behavior. ",
      planLink,
    ],
  },
  {
    q: "Does Min work in group chats?",
    a: [
      "Fun uses global defaults for groups. Pro adds per-chat overrides. ",
      planLink,
    ],
  },
  {
    q: "What can Pro do that Fun cannot?",
    a: "Pro adds WhatsApp API access, branding removal and per-chat controls. Fun includes incoming summaries, transcripts and translation.",
  },
  {
    q: "What is the Agent plan?",
    a: "Agent is the coming-soon tier where Min becomes the broader WhatsApp assistant for monitoring, escalation, digests, and follow-through. It is not purchasable yet.",
  },
];

function renderAnswer(answer, faqIndex, tx, planBehaviorHref) {
  if (!Array.isArray(answer)) {
    return tx(answer);
  }
  return answer.map((part, partIndex) => {
    const key = `faq-${faqIndex}-part-${partIndex}`;
    if (typeof part === "string") {
      return <span key={key}>{tx(part)}</span>;
    }
    const href = part.planBehavior ? planBehaviorHref : part.href;
    const isExternal = /^https?:\/\//.test(href);
    return (
      <a
        key={key}
        href={href}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noreferrer" : undefined}
        className="font-medium text-primary underline-offset-4 hover:underline"
      >
        {tx(part.text)}
      </a>
    );
  });
}

export default function FAQ() {
  const { locale, tx } = useI18n();
  const navigate = useNavigate();
  const planBehaviorHref = buildPlanComparisonPath(locale);
  const faqs = [...MIN_TRUST_FAQS, ...productFaqs];

  return (
    <section id="faq" className="landing-faq">
      <div className="landing-shell landing-faq__inner">
        <header className="landing-faq__heading">
          <h2>{tx("Questions? Answers.")}</h2>
        </header>

        <Accordion type="single" collapsible className="landing-faq__accordion">
          {faqs.map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="landing-faq__item">
              <AccordionTrigger className="landing-faq__trigger">
                {tx(faq.q)}
              </AccordionTrigger>
              <AccordionContent className="landing-faq__answer">
                {renderAnswer(faq.a, i, tx, planBehaviorHref)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <aside className="landing-faq__support">
          <div>
            <p className="landing-faq__support-label">{tx("Need Help Fast?")}</p>
            <h3>{tx("Reach Min support on WhatsApp")}</h3>
          </div>
          <p>
            {tx("Ask anything about setup, linking, relinks, or billing. We man it, and you can message us directly on WhatsApp.")}
          </p>
          <Button
            className="rounded-full landing-faq__support-button"
            onClick={() => {
              if (ONBOARDING_DESIGN_MODE) {
                const homePath = locale === "en" ? "/" : `/l/${encodeURIComponent(locale)}`;
                navigate(`${homePath}?prototype=qr`);
                return;
              }
              void openMinSupportChat({ sourceStep: "landing_faq_support" });
            }}
          >
            <MessageCircle className="h-4 w-4" />
            {tx("Reach Min support")}
          </Button>
        </aside>
      </div>
    </section>
  );
}
